import { Component } from '@angular/core';
import { ContentBlockType, Solver, SolverComponent } from '@examdojo/study-notes';
import { SuperLatexContent } from '@examdojo/super-latex';

@Component({
  selector: 'exam-solver-demo',
  standalone: true,
  imports: [SolverComponent],
  templateUrl: './solver-demo.component.html',
  styleUrls: ['./solver-demo.component.scss'],
})
export class SolverDemoComponent {
  private readonly step1Content: SuperLatexContent = {
    latex_nodes: [
      {
        id: 'line-1',
        latex: `$\\frac{d}{dx}(\\htmlData{anchor=derivative-x3}{x^3} + {2x^2} - 5x + 1) = \\htmlData{anchor=result-3x2}{3x^2} + {4x} - \\htmlData{anchor=result-minus5}{5}$`,
      },
      {
        id: 'line-2',
        latex: `$\\htmlData{anchor=fprime}{f'(x)} = 3x^2 + 4x - 5$`,
      },
      {
        id: 'line-3',
        latex: `$\\htmlData{anchor=yprime}{y'} = 3x^2 + 4x - 5$`,
      },
    ],
  };

  readonly derivativeSolver: Solver = {
    id: 'derivative-example',
    name: 'Derivative Walkthrough',
    stem_content_items: [
      {
        id: 'derivative-stem-1',
        type: ContentBlockType.Text,
        text: 'Find the derivative of the function $f(x) = x^3 + 2x^2 - 5x + 1$',
      },
    ],
    slides: [
      {
        name: 'Just a demo slide',
        initial_super_latex_content: this.step1Content,
        initial_description_content: [
          {
            type: ContentBlockType.Text,
            id: 'desc-1',
            text: 'Step 1: Just the equations (no connections or annotations)',
          },
        ],
        steps: [
          {
            title: 'Just the equations (no connections or annotations)',
            // No actions means it stays at initial state
          },
          {
            title: 'Connections appear between related terms',
            super_latex_actions: [
              {
                type: 'add_connections',
                connections: [
                  {
                    id: 'x3-to-3x2',
                    from_anchor: 'derivative-x3',
                    to_anchor: 'result-3x2',
                    style: {
                      color: 'sky',
                      width: 2,
                      line_style: 'dashed',
                      from_socket: 'Top',
                      to_socket: 'Top',
                      stub: 15,
                    },
                  },
                  {
                    id: 'fprime-to-minus5',
                    from_anchor: 'fprime',
                    to_anchor: 'result-minus5',
                    style: {
                      color: 'purple',
                      width: 2,
                      line_style: 'solid',
                      from_socket: 'Top',
                      to_socket: 'Bottom',
                      stub: 20,
                    },
                  },
                ],
              },
            ],
            description_content_actions: [
              {
                type: 'replace_item',
                item_id: 'desc-1',
                new_item: {
                  type: ContentBlockType.Text,
                  id: 'desc-1',
                  text: 'Step 2: Connections appear between related terms',
                },
              },
            ],
          },
          {
            title: "Annotation added (click $f'(x)$ to see it) + $y'$ highlighted in green",
            super_latex_actions: [
              {
                type: 'add_annotations',
                annotations: [
                  {
                    id: 'fprime-annotation',
                    target_anchor: 'fprime',
                    content: "This is **Lagrange's notation** for derivatives",
                    trigger: 'click',
                    placement: 'top',
                  },
                ],
              },
              {
                type: 'add_highlights',
                highlights: {
                  yprime: { style: 'text-color-with-background', color: 'green' },
                },
              },
            ],
            description_content_actions: [
              {
                type: 'replace_item',
                item_id: 'desc-1',
                new_item: {
                  type: ContentBlockType.Text,
                  id: 'desc-1',
                  text: "Step 3: Annotation added (click $f'(x)$ to see it) + $y'$ highlighted in green",
                },
              },
            ],
          },
          {
            title: 'New equation inserted (Leibniz notation $\\frac{dy}{dx}$)',
            super_latex_actions: [
              {
                type: 'insert_nodes',
                after_id: 'line-2',
                nodes: [
                  {
                    id: 'line-2-5',
                    latex: `$\\frac{dy}{dx} = 3x^2 + 4x - 5$`,
                  },
                ],
              },
            ],
            description_content_actions: [
              {
                type: 'replace_item',
                item_id: 'desc-1',
                new_item: {
                  type: ContentBlockType.Text,
                  id: 'desc-1',
                  text: 'Step 4: New equation inserted (Leibniz notation $\\frac{dy}{dx}$)',
                },
              },
              {
                type: 'add_items',
                position: 'end',
                items: [
                  {
                    type: ContentBlockType.Text,
                    id: 'desc-new-line',
                    text: 'This is a **new line of text** that should animate in.',
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  };

  readonly quadraticSolver: Solver = {
    id: 'example-1-3-2',
    name: 'EXAMPLE 1.3.2',
    stem_content_items: [
      {
        id: 'stem-1',
        type: ContentBlockType.Text,
        text: 'Let $f(x) = x^3$. Find the equation of the normal at $x = 2$.',
      },
    ],
    slides: [
      {
        name: 'Determine the number of possible solutions',
        initial_super_latex_content: {
          latex_nodes: [
            { id: 'step1-page1-line1', latex: '$\\Delta = b^2 - 4ac$' },
            {
              id: 'step1-page1-line2',
              latex: '$= (-8)^2 - 4 \\cdot 3 \\cdot 4 = 16 > 0$',
            },
          ],
        },
        initial_description_content: [
          {
            type: ContentBlockType.Text,
            id: 'desc-q1',
            text: 'The quadratic function has two distinct **real** roots',
          },
        ],
      },
      {
        name: 'Use the quadratic formula to solve for the solutions',
        initial_super_latex_content: {
          // This slide needs its own initial content.
          // In previous model, "slide" had steps, but "initial_super_latex_content" is per SLIDE.
          // But the slide 2 content in demo seemed to start fresh or continue?
          // The demo had "step2-page1" nodes.
          // Since it's a new slide, it resets to its own initial content.
          latex_nodes: [
            {
              id: 'step2-page1-line1',
              latex: '$x_{1,2} = \\frac{-b \\pm \\sqrt{\\Delta}}{2a}$',
            },
            {
              id: 'step2-page1-line2',
              latex: '$x_{1,2} = \\frac{8 \\pm \\sqrt{16}}{2 \\cdot 3}$',
            },
            {
              id: 'step2-page1-line3',
              latex: '$x_{1} = 2, \\quad x_{2} = \\frac{2}{3}$',
            },
          ],
        },
        initial_description_content: [
          {
            type: ContentBlockType.Text,
            id: 'desc-q2',
            text: 'The quadratic function has two distinct **real** roots',
          },
        ],
      },
    ],
  };
}
